import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';
// Import Types
import type { ListHeaderMenuItem, ResolvedListHeaderAction } from 'types';
// Import UI Components
import { Button, Option, Popover, Tooltip } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { ListHeaderActionPlacementEnum } from 'enums';
// Import Styles
import styles from './listHeader.module.scss';

export interface ListHeaderActionsProps {
    actions: ResolvedListHeaderAction[];
    menuTooltip?: string;
    menuIcon?: string;
}

/**
 * Right part of the compact list header: header actions in the configured order,
 * followed by a single three-dot menu holding every action placed in the menu.
 * Rendered into gene-ui `ComboTable`'s `titleActions` slot.
 */
export const ListHeaderActions: FC<ListHeaderActionsProps> = ({
    actions,
    menuTooltip,
    menuIcon = 'bc-icon-more-vertical',
}) => {
    const { t } = useTranslation();

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const headerActions = actions.filter(
        ({ placement, headerNode }) => placement === ListHeaderActionPlacementEnum.Header && !isNil(headerNode),
    );
    const menuActions = actions.filter(
        ({ placement, menuItem }) => placement === ListHeaderActionPlacementEnum.Menu && !isNil(menuItem),
    );

    const handleMenuToggle = () => {
        setIsMenuOpen((isOpen) => !isOpen);
    };

    const handleMenuItemClick = (menuItem: ListHeaderMenuItem) => {
        setIsMenuOpen(false);
        menuItem.onClick();
    };

    return (
        <div className={styles.listHeaderActions}>
            {headerActions.map(({ id, headerNode }) => (
                <div key={id} className={styles.listHeaderActionsItem}>
                    {headerNode}
                </div>
            ))}

            {menuActions.length > 0 ? (
                // gene-ui Popover injects its toggle `onClick` into its direct child, while gene-ui Tooltip only
                // forwards mouse enter/leave to its child. The direct child therefore has to be a DOM element,
                // not the Tooltip, or clicking the trigger never opens the menu.
                <Popover
                    isOpen={isMenuOpen}
                    toggleHandler={handleMenuToggle}
                    extendTargetWidth={false}
                    position="bottom"
                    align="end"
                    scrollbarNeeded={false}
                    Content={
                        <>
                            {menuActions.map(({ id, menuItem }) =>
                                isNil(menuItem) ? null : (
                                    <Option
                                        key={id}
                                        title={menuItem.label}
                                        icon={menuItem.icon}
                                        disabled={menuItem.isDisabled}
                                        onClick={() => handleMenuItemClick(menuItem)}
                                    />
                                ),
                            )}
                        </>
                    }
                >
                    <div className={styles.listHeaderActionsItem}>
                        <Tooltip text={menuTooltip ?? t(l.MoreActions)}>
                            <Button icon={menuIcon} appearance="minimal" color="primary" />
                        </Tooltip>
                    </div>
                </Popover>
            ) : null}
        </div>
    );
};
